import React, { useState, useEffect, useRef } from 'react'
import Header from './Header.jsx'
import { Link } from 'react-router-dom'
import { useLocation } from 'react-router-dom';
import useApiRequest from './useApiRequest.jsx';
import { domain } from './Domain.jsx';
import { useLoadingContext } from './LoadingContext.jsx';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import notify from './Nots.jsx'
import { motion, AnimatePresence } from 'framer-motion';
import { 
    Ticket, Calendar, MapPin, User, Phone, Mail, 
    CreditCard, Users, Sofa, DollarSign, ArrowRight,
    CheckCircle, AlertCircle, Star, Clock
} from 'lucide-react';

const TicketPurchase = () => {
    const { getRequest, postRequest } = useApiRequest()
    const { loading, setLoading } = useLoadingContext()
    const location = useLocation();
    const [show, setShow] = useState(false);
    const [showNames, setShowNames] = useState(false);
    const [sameNames, setSameNames] = useState(true);
    // Function to get query parameters
    const query = new URLSearchParams(location.search);
    const ticketId = query.get('ticket'); // Retrieve the 'ticket' parameter
    const eventSlug = query.get('event');// Retrieve the 'event' parameter

    const initial = {
        firstName: "",
        lastName: "",
        paymentMode: "",
        phoneNumber: "",
        email: "",
        sameNames: sameNames,
        eventSlug: eventSlug,
        ticketId: ticketId,
        qty: 1
    }
    const [ticketData, setTicketData] = useState(initial)
    const [individualNames, setIndividualNames] = useState(['']);
    const [phoneError, setPhoneError] = useState('');

    // Re-validate phone number when payment mode changes
    useEffect(() => {
        if (ticketData.phoneNumber) {
            validatePhoneNumber(ticketData.phoneNumber, ticketData.paymentMode);
        }
    }, [ticketData.paymentMode]);

    // Phone number validation function
    const validatePhoneNumber = (phoneNumber, paymentMode) => {
        if (!phoneNumber.trim()) {
            setPhoneError('');
            return true;
        }

        // Network-specific validation
        if (paymentMode === 'MTN MOBILE MONEY') {
            const mtnPrefixes = ['077', '078', '031', '039', '076'];
            const hasValidMtnPrefix = mtnPrefixes.some(prefix => phoneNumber.startsWith(prefix));
            
            if (!hasValidMtnPrefix) {
                setPhoneError('MTN number must start with 077, 078, 031, 039, or 076');
                return false;
            }
        } else if (paymentMode === 'AIRTEL MONEY') {
            const airtelPrefixes = ['075', '070', '074'];
            const hasValidAirtelPrefix = airtelPrefixes.some(prefix => phoneNumber.startsWith(prefix));
            
            if (!hasValidAirtelPrefix) {
                setPhoneError('Airtel number must start with 075, 070, or 074');
                return false;
            }
        }

        // Basic phone number validation (should be at least 10 digits)
        const cleanNumber = phoneNumber.replace(/\D/g, '');
        if (cleanNumber.length < 10) {
            setPhoneError('Phone number must be at least 10 digits');
            return false;
        }

        setPhoneError('');
        return true;
    };

    // Function to handle changes in form inputs
    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (type === 'checkbox') {
            setTicketData((prevEventData) => ({
                ...prevEventData,
                [name]: checked
            }));
        } else {
            setTicketData((prevEventData) => ({
                ...prevEventData,
                [name]: value
            }));

            // Validate phone number when it changes
            if (name === 'phoneNumber') {
                validatePhoneNumber(value, ticketData.paymentMode);
            }
        }
    };

    const [selectedSeats, setSelectedSeats] = useState([]);
    const [totalSeats, setTotalSeats] = useState(0); // Adjust this as needed
    const [paymentStatus, setPaymentStatus] = useState('pending') // pending, success, failed

    const [eventDetails, setEventDetails] = useState(null)
    const [ticketDetails, setTicketDetails] = useState(null)
    const emptyArray = Array.from({ length: 1000 }, (_, index) => index);
    const [qty, setQty] = useState(1)
    const redirectUrl = useRef()
    const totalAmount = useRef()
    const paymentCheckInterval = useRef(null)

    // Function to format the date to 'August 3, 2023'
    function formatDate(date) {
        const options = { year: 'numeric', month: 'long', day: 'numeric' };
        return date.toLocaleDateString('en-US', options);
    }

    const truncateString = (str) => {
        return str.length > 170 ? str.substring(0, 169) + "..." : str;
    }


    const fetchEventDetails = async () => {
        const details = await getRequest(`${domain}/event-details/${eventSlug}`)
        console.log(details)
        setEventDetails(details.body.details)
    }

    const fetchTicketDetails = async () => {
        const details = await getRequest(`${domain}/ticket-information/${ticketId}`)
        setTicketDetails(details.body)
        totalAmount.current = details.body.ticket_price + details.body.transaction_fees
        setTotalSeats(details.body.no_seats_available)
    }

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);

            try {
                // Perform all fetch requests in parallel and wait for all of them to complete
                await Promise.all([fetchEventDetails(), fetchTicketDetails()]);
            } catch (error) {
                console.error('Error fetching data:', error);
            } finally {
                // Set loading to false when all requests are completed (or error occurs)
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    useEffect(() => {
        setTicketData((prevEventData) => ({ ...prevEventData, sameNames: sameNames }))
    }, [sameNames])

    const handleChange = (e) => {
        const value = e.target.value
        totalAmount.current = (ticketDetails.ticket_price * value) + ticketDetails.transaction_fees
        setTicketData((prevEventData) => ({ ...prevEventData, qty: value }))
    }

    const handleClose = () => {
        setShow(false)
        setPaymentStatus('pending')
        // Clear any payment checking intervals
        if (paymentCheckInterval.current) {
            clearInterval(paymentCheckInterval.current)
            paymentCheckInterval.current = null
        }
    };
    const handleShow = () => setShow(true);

    const handleModalClose = () => { setShowNames(false); setSameNames(true) }; //; setSameNames(true) 
    const handleModalShow = () => setShowNames(true);

    // Function to check payment status
    const checkPaymentStatus = async (referenceId) => {
        try {
            const response = await fetch(`${domain}/check-payment-status/${referenceId}`)
            const data = await response.json()
            
            if (data.status === 'SUCCESSFUL') {
                setPaymentStatus('success')
                notify("Payment successful! Your tickets have been purchased.", "success")
                // Close modal after a short delay
                setTimeout(() => {
                    handleClose()
                    // Redirect to tickets page
                    window.location.href = `/generated-tickets?id=${data.ticket_id}`
                }, 2000)
            } else if (data.status === 'FAILED') {
                setPaymentStatus('failed')
                notify("Payment failed. Please try again.", "error")
            }
        } catch (error) {
            console.error('Error checking payment status:', error)
        }
    }

    useEffect(() => {
        if (sameNames === false) {
            handleModalShow()
        }
    }, [sameNames])


    const handleNameChange = (index, field, value) => {
        const updatedNames = [...individualNames];
        updatedNames[index] = { ...updatedNames[index], [field]: value };
        setIndividualNames(updatedNames);
    };
    const validateForm = () => {
        const { firstName, lastName, paymentMode, phoneNumber, email, qty } = ticketData;

        if (
            !firstName.trim() ||
            !lastName.trim() ||
            !paymentMode.trim() ||
            !phoneNumber.trim() ||
            !email.trim() ||
            !qty
        ) {
            notify('Please fill out all required fields.', 'error');
            return false;
        }

        // Only validate seat selection if the ticket requires seat numbers
        const requiresSeats = ticketDetails && ticketDetails.allow_guests_book_seat_numbers && ticketDetails.no_seats_available > 0;
        if (requiresSeats && selectedSeats.length !== parseInt(ticketData.qty, 10)) {
            notify(`You must select exactly ${ticketData.qty} seat(s).`, 'error');
            return false;
        }

        // Validate phone number
        if (!validatePhoneNumber(phoneNumber, paymentMode)) {
            notify(phoneError, 'error');
            return false;
        }

        if (!sameNames && individualNames.length !== parseInt(ticketData.qty, 10)) {
            notify('Please provide individual names for each ticket.', 'error');
            return false;
        }

        if (!sameNames) {
            for (let i = 0; i < individualNames.length; i++) {
                const { firstName, lastName } = individualNames[i] || {};
                if (!firstName?.trim() || !lastName?.trim()) {
                    notify(`Please fill in both first and last names for ticket ${i + 1}.`, 'error');
                    return false;
                }
            }
        }

        return true;
    };


    const handlePayment = async () => {
        if (!validateForm()) return; // validation check
        setLoading(true)
        
        // For MTN and Airtel payments, show modal immediately after a short delay
        const formData = new FormData()
        for (const [key, value] of Object.entries(ticketData)) {
            // Append each key-value pair to the FormData object
            formData.append(key, value);
        }
        formData.append("description", eventDetails ? eventDetails.event_plot_name : "")
        formData.append("amount", totalAmount.current)
        //formData.append("qty", qty)

        // Create a names array containing full names
        const names = [];


        if (!sameNames) {
            names.push(...individualNames.map((name) => `${name.firstName} ${name.lastName}`));

            // Append names as a single key in formData
            formData.append('names', JSON.stringify(names));
        }
        //add seat numbers if any (only if seats are required)
        const requiresSeatsForPayment = ticketDetails && ticketDetails.allow_guests_book_seat_numbers && ticketDetails.no_seats_available > 0;
        formData.append("seatNumbers", requiresSeatsForPayment ? JSON.stringify(selectedSeats) : JSON.stringify([]))

        const response = await postRequest(`${domain}/make-online-payment`, formData, true)
        console.log(response)

        // Extract error message from various possible locations
        const getErrorMessage = (response) => {
            if (response.message) return response.message;
            if (response.details?.error) return response.details.error;
            if (response.body?.error) return response.body.error;
            if (response.error) return response.error;
            return "An error occurred while processing your payment. Please try again.";
        };

        // Check if there's an error in the response
        const hasError = response.success === false || response.error || response.message || response.details?.error || response.body?.error;
        const errorMessage = hasError ? getErrorMessage(response) : null;

        if (ticketData['paymentMode'] === 'ONLINE CARDS') {
            if (hasError) {
                notify(errorMessage, "error")
                setLoading(false)
            } else if (response.body && response.body.redirect_url) {
                redirectUrl.current = response.body.redirect_url
                setLoading(false)
                handleShow()
            }
        } else {
            // For MTN and Airtel payments, handle response
            if (ticketData['paymentMode'] === 'MTN MOBILE MONEY' || ticketData['paymentMode'] === 'AIRTEL MONEY') {
                if (hasError) {
                    notify(errorMessage, "error")
                    setLoading(false)
                    handleClose() // Close modal on error
                    return // Stop processing - don't show success message or open modal
                }
                
                // Payment initiated successfully - check if it's already completed (SANDBOX mode)
                console.log('Mobile money payment response:', response)
                
                // If payment is successful, close modal and redirect
                if (response.body && response.body.ticket && response.body.ticket.status === 'SUCCESSFUL') {
                    notify("Payment successful! Your tickets have been purchased.", "success")
                    handleClose()
                    // Redirect to tickets or success page
                    window.location.href = `/generated-tickets?id=${response.body.ticket.info}`
                } else {
                    // Payment is pending - show success message and modal only when payment is actually initiated
                    if (response.body && (response.body.reference_id || response.body.ticket)) {
                        // Show success message only when payment is actually initiated
                        if (ticketData['paymentMode'] === 'MTN MOBILE MONEY') {
                            notify("MTN Mobile Money payment initiated! Please check your phone for the payment request.", "success")
                        } else if (ticketData['paymentMode'] === 'AIRTEL MONEY') {
                            notify("Airtel Money payment initiated! Please check your phone for the payment request.", "success")
                        }
                        
                        // Show modal after a short delay
                        setTimeout(() => {
                            handleShow()
                            setLoading(false)
                        }, 500)
                        
                        console.log('Payment is pending, waiting for user to complete on phone')
                        setPaymentStatus('pending')
                        
                        // Start polling for payment status every 3 seconds
                        if (response.body && response.body.reference_id) {
                            paymentCheckInterval.current = setInterval(() => {
                                checkPaymentStatus(response.body.reference_id)
                            }, 3000)
                        }
                    } else {
                        // No valid response body - something went wrong
                        setLoading(false)
                    }
                }
            } else {
                // For other payment methods
                if (hasError) {
                    notify(errorMessage, "error")
                    setLoading(false)
                } 
                else if (response.body && response.body.message) {
                    notify(response.body.message, "success")
                    handleShow()
                    setLoading(false)
                }
            }
        }
    }

    const handleSeatSelection = (seatNumber, alreadyBookedSeats) => {
        // Check if seat is already booked
        if (alreadyBookedSeats.includes(seatNumber)) {
            notify(`Seat number ${seatNumber} is already booked, please select another seat!`, 'error')
            return
        }
        
        // If seat is already selected, remove it from the selectedSeats array
        if (selectedSeats.includes(seatNumber)) {
            setSelectedSeats((prev) => prev.filter(seat => seat !== seatNumber));
            console.log('Seat deselected:', seatNumber, 'Current selected seats:', selectedSeats.filter(seat => seat !== seatNumber));
        } else if (selectedSeats.length >= ticketData.qty) {
            // Prevent selecting more seats than the quantity of tickets
            notify(`You can only select up to ${ticketData.qty} seats. Please increase the number of tickets if you want more seats.`, 'error');
            return; // Prevent further selection
        } else {
            // If seat is not selected yet, add it to the selectedSeats array
            setSelectedSeats((prev) => [...prev, seatNumber]);
            console.log('Seat selected:', seatNumber, 'Current selected seats:', [...selectedSeats, seatNumber]);
        }
    };

    const handleModalSave = () => {
        // Check if the number of names matches the quantity of tickets
        if (individualNames.length !== parseInt(ticketData.qty)) {
            alert("Please provide names for all tickets before proceeding.");
            return; // Prevent closing the modal
        }

        // If everything is fine, close the modal and maintain that the user needs individual names for all the tickets that they are buying
        setShowNames(false)
    };



    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 via-orange-50 to-teal-50">
            <Header />
            
            <div className="container mx-auto px-4 py-6 md:py-8">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="max-w-7xl mx-auto"
                >
                    {/* Header Section */}
                    <div className="mb-6 md:mb-8">
                        <div className="flex items-center gap-3 md:gap-4 mb-4">
                            <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-br from-orange-400 to-orange-500 rounded-xl flex items-center justify-center">
                                <Ticket className="w-5 h-5 md:w-6 md:h-6 !text-white" />
                            </div>
                            <div>
                                <h1 className="text-xl md:text-3xl font-bold text-gray-800">
                                    Purchase Tickets
                                </h1>
                                <p className="text-sm md:text-base text-gray-600">
                                    {eventDetails && eventDetails.event_plot_name}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
                        {/* Main Form */}
                        <div className="lg:col-span-2">
                            <motion.div
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                className="bg-white rounded-2xl shadow-lg border border-gray-100 p-4 md:p-8"
                            >
                                {/* Event Details */}
                                <div className="mb-6 md:mb-8">
                                    <div className="bg-gradient-to-r from-orange-50 to-teal-50 rounded-xl p-4 md:p-6 border border-orange-200">
                                        <div className="flex items-start gap-3 md:gap-4">
                                            <div className="w-12 h-12 md:w-16 md:h-16 bg-gradient-to-br from-orange-400 to-orange-500 rounded-xl flex items-center justify-center">
                                                <Calendar className="w-6 h-6 md:w-8 md:h-8 !text-white" />
                                            </div>
                                            <div className="flex-1">
                                                <h3 className="text-lg md:text-xl font-bold text-gray-800 mb-2">
                                                    {eventDetails && eventDetails.event_plot_name}
                                                </h3>
                                                <div className="space-y-2 text-xs md:text-sm">
                                                    <div className="flex items-center gap-2 text-orange-600">
                                                        <Calendar className="w-3 h-3 md:w-4 md:h-4" />
                                                        <span className="font-medium">
                                                            {eventDetails && formatDate(new Date(eventDetails.start_date_event))}
                                                        </span>
                                                    </div>
                                                    <div className="flex items-center gap-2 text-gray-600">
                                                        <MapPin className="w-3 h-3 md:w-4 md:h-4" />
                                                        <span>{eventDetails && eventDetails.venue_name}</span>
                                                    </div>
                                                    <div className="flex items-center gap-2 text-gray-600">
                                                        <User className="w-3 h-3 md:w-4 md:h-4" />
                                                        <span>Organiser: {eventDetails && eventDetails.organiser.organiser_name}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Form Fields */}
                                <div className="space-y-4 md:space-y-6">
                                    {/* Name Fields */}
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-3">
                                            Personal Information
                                        </label>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                                            <div>
                                                <label className="block text-sm text-gray-600 mb-2">First Name</label>
                                                <input 
                                                    type="text" 
                                                    name='firstName' 
                                                    onChange={handleInputChange} 
                                                    placeholder="Enter your first name" 
                                                    className="w-full px-3 py-2 md:px-4 md:py-3 text-sm border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200" 
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm text-gray-600 mb-2">Last Name</label>
                                                <input 
                                                    type="text" 
                                                    name='lastName' 
                                                    onChange={handleInputChange} 
                                                    placeholder="Enter your last name" 
                                                    className="w-full px-3 py-2 md:px-4 md:py-3 text-sm border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200" 
                                                />
                                            </div>
                                        </div>
                    </div>

                                    {/* Payment Mode */}
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-3">
                                            Payment Method
                                        </label>
                                        <select 
                                            className="w-full px-3 py-2 md:px-4 md:py-3 text-sm border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200" 
                                            name='paymentMode' 
                                            onChange={handleInputChange}
                                        >
                                            <option value="" disabled selected>Choose payment method</option>
                                            {
                                            /*<option value="MTN MOBILE MONEY">MTN Mobile Money</option>
                                            <option value="AIRTEL MONEY">Airtel Money</option>
                                            */}
                                            <option value="ONLINE CARDS">MTN Mobile Money</option>
                                            <option value="ONLINE CARDS">Airtel Money</option>
                                            <option value="ONLINE CARDS">Online Cards</option>
                        </select>
                    </div>

                                    {/* Number of Tickets */}
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-3">
                                            Number of Tickets
                                        </label>
                                        <select 
                                            className="w-full px-3 py-2 md:px-4 md:py-3 text-sm border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200" 
                                            value={ticketData.qty} 
                                            onChange={handleChange}
                                        >
                            {emptyArray.map(item =>
                                <option key={item} value={item + 1}>{item + 1}</option>
                            )}
                        </select>
                    </div>

                                    {/* Ticket Names Configuration */}
                    {ticketData.qty > 1 && (
                                        <div className="bg-gradient-to-r from-orange-50 to-teal-50 rounded-xl p-4 md:p-6 border border-orange-200">
                                            <div className="flex items-center gap-2 md:gap-3 mb-3 md:mb-4">
                                                <div className="w-6 h-6 md:w-8 md:h-8 bg-gradient-to-br from-orange-400 to-orange-500 rounded-lg flex items-center justify-center">
                                                    <Users className="w-3 h-3 md:w-4 md:h-4 !text-white" />
                                                </div>
                            <div>
                                                    <label className="block text-sm font-semibold text-gray-800">
                                                        Ticket Names Configuration
                                                    </label>
                                                    <p className="text-xs text-gray-600">Choose how to assign names to your tickets</p>
                                                </div>
                                            </div>
                                            
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                                                <motion.label 
                                                    className={`relative p-3 md:p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 ${
                                                        sameNames 
                                                            ? 'bg-orange-500 border-orange-600 shadow-lg' 
                                                            : 'bg-white border-gray-200 hover:border-orange-300 hover:bg-orange-50'
                                                    }`}
                                                    whileHover={{ scale: 1.02 }}
                                                    whileTap={{ scale: 0.98 }}
                                                >
                                                    <input 
                                                        type="radio" 
                                                        value="yes" 
                                                        checked={sameNames} 
                                                        onChange={() => setSameNames(true)}
                                                        className="sr-only"
                                                    />
                                                    <div className="flex items-start gap-2 md:gap-3">
                                                        <div className={`w-4 h-4 md:w-5 md:h-5 rounded-full border-2 flex items-center justify-center mt-0.5 ${
                                                            sameNames 
                                                                ? 'border-white bg-white' 
                                                                : 'border-gray-400'
                                                        }`}>
                                                            {sameNames && (
                                                                <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                                                            )}
                                                        </div>
                                                        <div className="flex-1">
                                                            <div className="flex items-center gap-2 mb-1">
                                                                <Users className={`w-3 h-3 md:w-4 md:h-4 ${sameNames ? '!text-white' : 'text-orange-500'}`} />
                                                                <span className={`text-sm md:text-base font-medium ${sameNames ? '!text-white' : 'text-gray-800'}`}>
                                                                    Same Name for All
                                                                </span>
                                                            </div>
                                                            <p className={`text-xs ${sameNames ? 'text-orange-100' : 'text-gray-600'}`}>
                                                                Use one name for all {ticketData.qty} tickets
                                                            </p>
                            </div>
                        </div>
                                                </motion.label>
                                                
                                                <motion.label 
                                                    className={`relative p-3 md:p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 ${
                                                        !sameNames 
                                                            ? 'bg-teal-500 border-teal-600 shadow-lg' 
                                                            : 'bg-white border-gray-200 hover:border-teal-300 hover:bg-teal-50'
                                                    }`}
                                                    whileHover={{ scale: 1.02 }}
                                                    whileTap={{ scale: 0.98 }}
                                                >
                                                    <input 
                                                        type="radio" 
                                                        value="no" 
                                                        checked={!sameNames} 
                                                        onChange={() => setSameNames(false)}
                                                        className="sr-only"
                                                    />
                                                    <div className="flex items-start gap-2 md:gap-3">
                                                        <div className={`w-4 h-4 md:w-5 md:h-5 rounded-full border-2 flex items-center justify-center mt-0.5 ${
                                                            !sameNames 
                                                                ? 'border-white bg-white' 
                                                                : 'border-gray-400'
                                                        }`}>
                                                            {!sameNames && (
                                                                <div className="w-2 h-2 bg-teal-500 rounded-full"></div>
                                                            )}
                                                        </div>
                                                        <div className="flex-1">
                                                            <div className="flex items-center gap-2 mb-1">
                                                                <User className={`w-3 h-3 md:w-4 md:h-4 ${!sameNames ? '!text-white' : 'text-teal-500'}`} />
                                                                <span className={`text-sm md:text-base font-medium ${!sameNames ? '!text-white' : 'text-gray-800'}`}>
                                                                    Different Names
                                                                </span>
                                                            </div>
                                                            <p className={`text-xs ${!sameNames ? 'text-teal-100' : 'text-gray-600'}`}>
                                                                Assign unique names to each ticket
                                                            </p>
                                                        </div>
                                                    </div>
                                                </motion.label>
                                            </div>
                                            
                                            {!sameNames && (
                                                <motion.div
                                                    initial={{ opacity: 0, height: 0 }}
                                                    animate={{ opacity: 1, height: 'auto' }}
                                                    transition={{ duration: 0.3 }}
                                                    className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg"
                                                >
                                                    <div className="flex items-center gap-2 text-blue-700">
                                                        <AlertCircle className="w-4 h-4" />
                                                        <span className="text-sm font-medium">Individual names will be required during payment</span>
                                                    </div>
                                                </motion.div>
                                            )}
                            </div>
                                    )}

                                    {/* Seat Selection - Only show if seats are required */}
                                    {ticketDetails && ticketDetails.allow_guests_book_seat_numbers && ticketDetails.no_seats_available > 0 && (
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-3">
                                            Select Seats ({selectedSeats.length}/{ticketData.qty})
                                        </label>
                                        

                                        <div className="bg-gray-50 rounded-xl p-4 md:p-6">
                                            <div className="grid grid-cols-6 sm:grid-cols-8 md:grid-cols-12 lg:grid-cols-16 gap-1 md:gap-2">
                            {totalSeats > 0 && Array.from({ length: totalSeats }, (_, index) => index).map(seatNumber => {
                                                    // Safely parse the seats_booked data
                                                    let bookedSeats = [];
                                                    try {
                                                        bookedSeats = JSON.parse(ticketDetails?.seats_booked || '[]');
                                                    } catch (error) {
                                                        console.error('Error parsing seats_booked:', error);
                                                        bookedSeats = [];
                                                    }
                                                    
                                                    const seatBooked = bookedSeats.includes(seatNumber + 1);
                                                    const isSelected = selectedSeats.includes(seatNumber + 1);
                                                    
                                                    // Debug logging for seat state
                                                    if (seatNumber + 1 === 1) { // Debug first seat
                                                        console.log(`Seat ${seatNumber + 1}:`, {
                                                            seatBooked,
                                                            isSelected,
                                                            bookedSeats,
                                                            selectedSeats
                                                        });
                                                    }
                                                    
                                                    // Determine the styling based on seat state
                                                    let seatClassName = '';
                                                    if (seatBooked) {
                                                        seatClassName = '!bg-red-200 !text-red-700 cursor-not-allowed !border-2 !border-red-300';
                                                    } else if (isSelected) {
                                                        seatClassName = '!bg-orange-500 !text-white shadow-lg !border-2 !border-orange-600';
                                                    } else {
                                                        seatClassName = 'bg-white hover:bg-orange-50 border border-gray-200 hover:border-orange-300';
                                                    }
                                                    
                                                    // Debug: Log the className for seat 7
                                                    if (seatNumber + 1 === 7) {
                                                        console.log('Seat 7 className:', seatClassName);
                                                        console.log('Seat 7 isSelected:', isSelected);
                                                    }
                                                    
                                return (
                                                        <motion.button
                                        key={seatNumber + 1}
                                        disabled={seatBooked}
                                                            onClick={() => handleSeatSelection(seatNumber + 1, bookedSeats)}
                                                            className={`p-1 md:p-2 rounded-lg transition-all duration-200 flex flex-col items-center justify-center ${seatClassName}`}
                                                            whileHover={!seatBooked ? { scale: 1.05 } : {}}
                                                            whileTap={!seatBooked ? { scale: 0.95 } : {}}
                                                        >
                                                            <Sofa className={`w-3 h-3 md:w-4 md:h-4 ${isSelected ? '!text-white' : seatBooked ? '!text-red-700' : 'text-gray-600'}`} />
                                                            <span className={`text-xs font-medium ${isSelected ? '!text-white' : seatBooked ? '!text-red-700' : 'text-gray-700'}`}>
                                                                {seatNumber + 1} {seatBooked ? 'B' : ''}{isSelected ? 'S' : ''}
                                                            </span>
                                                        </motion.button>
                                                    )
                                                })}
                                            </div>
                                            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4 mt-3 md:mt-4 text-xs md:text-sm">
                                                <div className="flex items-center gap-2">
                                                    <div className="w-3 h-3 md:w-4 md:h-4 bg-white border border-gray-200 rounded"></div>
                                                    <span>Available</span>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <div className="w-3 h-3 md:w-4 md:h-4 bg-orange-500 rounded"></div>
                                                    <span>Selected</span>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <div className="w-3 h-3 md:w-4 md:h-4 bg-red-200 rounded"></div>
                                                    <span>Booked</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    )}

                                    {/* Contact Information */}
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-3">
                                            Contact Information
                                        </label>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                                            <div>
                                                <label className="block text-sm text-gray-600 mb-2">
                                                    Phone Number
                                                    {ticketData.paymentMode === 'MTN MOBILE MONEY' && (
                                                        <span className="text-xs text-blue-600 ml-1">(077, 078, 031, 039, 076)</span>
                                                    )}
                                                    {ticketData.paymentMode === 'AIRTEL MONEY' && (
                                                        <span className="text-xs text-blue-600 ml-1">(075, 070, 074)</span>
                                                    )}
                                                </label>
                                                <div className="relative">
                                                    <Phone className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 ${phoneError ? 'text-red-400' : 'text-gray-400'}`} />
                                                    <input 
                                                        type="text" 
                                                        name='phoneNumber' 
                                                        onChange={handleInputChange} 
                                                        placeholder="Enter phone number" 
                                                        className={`w-full pl-10 pr-3 md:pr-4 py-2 md:py-3 text-sm border rounded-xl focus:ring-2 focus:border-transparent transition-all duration-200 ${
                                                            phoneError 
                                                                ? 'border-red-300 focus:ring-red-500 bg-red-50' 
                                                                : 'border-gray-300 focus:ring-orange-500'
                                                        }`}
                                                    />
                                                </div>
                                                {phoneError && (
                                                    <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                                                        <AlertCircle className="w-3 h-3" />
                                                        {phoneError}
                                                    </p>
                                                )}
                                            </div>
                                            <div>
                                                <label className="block text-sm text-gray-600 mb-2">Email Address</label>
                                                <div className="relative">
                                                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                                                    <input 
                                                        type="email" 
                                                        name='email' 
                                                        onChange={handleInputChange} 
                                                        placeholder="Enter email address" 
                                                        className="w-full pl-10 pr-3 md:pr-4 py-2 md:py-3 text-sm border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200" 
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Payment Button */}
                                    <motion.button 
                                        type="button" 
                                        onClick={handlePayment} 
                                        className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 !text-white px-4 md:px-6 py-3 md:py-4 rounded-xl font-semibold text-sm md:text-lg transition-all duration-200 flex items-center justify-center gap-2 md:gap-3 shadow-lg hover:shadow-xl"
                                        whileHover={{ scale: 1.02 }}
                                        whileTap={{ scale: 0.98 }}
                                    >
                                        <DollarSign className="w-4 h-4 md:w-5 md:h-5" />
                                        Make Payment
                                        <ArrowRight className="w-4 h-4 md:w-5 md:h-5" />
                                    </motion.button>
                                </div>
                            </motion.div>
                        </div>

                        {/* Order Summary */}
                        <div className="lg:col-span-1">
                            <motion.div
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                className="bg-white rounded-2xl shadow-lg border border-gray-100 p-4 md:p-6 sticky top-8"
                            >
                                <div className="flex items-center gap-2 md:gap-3 mb-4 md:mb-6">
                                    <div className="w-8 h-8 md:w-10 md:h-10 bg-gradient-to-br from-teal-400 to-teal-500 rounded-xl flex items-center justify-center">
                                        <Ticket className="w-4 h-4 md:w-5 md:h-5 !text-white" />
                                    </div>
                                    <div>
                                        <h3 className="text-sm md:text-base font-bold text-gray-800">Order Summary</h3>
                                        <p className="text-xs md:text-sm text-gray-600">Review your purchase</p>
                                    </div>
                                </div>

                                {/* Event Image */}
                                {eventDetails && (
                                    <div className="mb-4 md:mb-6">
                                        <img 
                                            src={domain + eventDetails.banner} 
                                            alt={eventDetails.event_plot_name}
                                            className="w-full h-32 md:h-48 object-cover rounded-xl shadow-md"
                                        />
                                    </div>
                                )}

                                {/* Summary Details */}
                                <div className="space-y-3 md:space-y-4">
                                    <div className="flex items-center justify-between p-2 md:p-3 bg-gray-50 rounded-lg">
                                        <span className="text-xs md:text-sm text-gray-600">Ticket Price</span>
                                        <span className="text-sm md:text-base font-semibold text-gray-800">
                                            Ugx {ticketDetails && ticketDetails.ticket_price.toLocaleString()}/=
                                        </span>
                                    </div>
                                    <div className="flex items-center justify-between p-2 md:p-3 bg-gray-50 rounded-lg">
                                        <span className="text-xs md:text-sm text-gray-600">Transaction Fees</span>
                                        <span className="text-sm md:text-base font-semibold text-gray-800">
                                            Ugx {ticketDetails && ticketDetails.transaction_fees.toLocaleString()}/=
                                        </span>
                                    </div>
                                    <div className="flex items-center justify-between p-2 md:p-3 bg-gray-50 rounded-lg">
                                        <span className="text-xs md:text-sm text-gray-600">Number of Tickets</span>
                                        <span className="text-sm md:text-base font-semibold text-green-600">{ticketData.qty}</span>
                                    </div>
                                    {ticketDetails && ticketDetails.allow_guests_book_seat_numbers && ticketDetails.no_seats_available > 0 && (
                                    <div className="flex items-center justify-between p-2 md:p-3 bg-gray-50 rounded-lg">
                                        <span className="text-xs md:text-sm text-gray-600">Seats Selected</span>
                                        <span className="text-sm md:text-base font-semibold text-green-600">{selectedSeats.length}</span>
                                    </div>
                                    )}
                                    <div className="flex items-center justify-between p-2 md:p-3 bg-orange-50 rounded-lg border border-orange-200">
                                        <span className="text-xs md:text-sm font-semibold text-orange-700">Total Amount</span>
                                        <span className="text-sm md:text-lg font-bold text-orange-600">
                                            Ugx {totalAmount.current && totalAmount.current.toLocaleString()}/=
                                        </span>
                        </div>
                    </div>

                                {/* Ticket Details */}
                                {ticketDetails && (
                                    <div className="mt-4 md:mt-6 p-3 md:p-4 bg-gradient-to-r from-orange-50 to-teal-50 rounded-xl border border-orange-200">
                                        <h4 className="text-sm md:text-base font-semibold text-gray-800 mb-2 md:mb-3">Ticket Details</h4>
                                        <div className="space-y-2 text-xs md:text-sm">
                                            <div className="flex items-center justify-between">
                                                <span className="text-gray-600">Type:</span>
                                                <span className="font-medium">{ticketDetails.ticket_type.name}</span>
                                            </div>
                                            <div className="flex items-center justify-between">
                                                <span className="text-gray-600">Category:</span>
                                                <span className="font-medium">{ticketDetails.ticket_category.name}</span>
                                            </div>
                    </div>
                    </div>
                                )}
                            </motion.div>
                </div>
                    </div>
                </motion.div>
            </div>

            {/* Payment Modal */}
            <Modal
                show={show}
                onHide={handleClose}
                backdrop="static"
                size="lg"
                aria-labelledby="contained-modal-title-vcenter"
                centered
                keyboard={false}
            >
                <Modal.Header className='bg-gradient-to-r from-green-500 to-green-600 !text-white flex items-center justify-between'>
                    <Modal.Title className="flex-1">Complete Payment Here</Modal.Title>
                    <button 
                        onClick={handleClose}
                        className="ml-auto p-2 hover:bg-white/20 rounded-full transition-colors duration-200"
                        aria-label="Close"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </Modal.Header>
                <Modal.Body>
                    <h4 className='fs-6 mb-4'>Ticket Purchase for {eventDetails && eventDetails.event_plot_name} - {eventDetails && eventDetails.event_category.name}</h4>
                    {ticketData['paymentMode'] === 'ONLINE CARDS' ?
                        <iframe src={redirectUrl.current} className='w-100' style={{ height: "50vh" }} title="Payment Form" />
                        :
                        <div className="text-center py-8">
                            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Phone className="w-8 h-8 text-green-600" />
                            </div>
                            <h5 className="text-lg font-semibold text-gray-800 mb-2">Complete Payment on Your Phone</h5>
                            <p className="text-gray-600 mb-4">Please check your phone to complete the payment process.</p>
                            
                            {/* Enhanced instructions for mobile money payments */}
                            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 md:p-6 mb-4">
                                <div className="flex items-start gap-3">
                                    <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                                        <Phone className="w-4 h-4 text-white" />
                                    </div>
                                    <div className="text-left">
                                        <h6 className="text-sm md:text-base font-semibold text-blue-800 mb-2">
                                            {ticketData['paymentMode'] === 'MTN MOBILE MONEY' ? 'MTN Mobile Money Payment' : 'Airtel Money Payment'}
                                        </h6>
                                        <div className="space-y-2 text-xs md:text-sm text-blue-700">
                                            <p className="flex items-center gap-2">
                                                <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                                                Check your phone for a payment request notification
                                            </p>
                                            <p className="flex items-center gap-2">
                                                <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                                                Enter your mobile money PIN when prompted
                                            </p>
                                            <p className="flex items-center gap-2">
                                                <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                                                Confirm the payment amount and complete the transaction
                                            </p>
                                            <p className="flex items-center gap-2">
                                                <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                                                You will receive a confirmation SMS once payment is successful
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            {/* Payment status indicator */}
                            {paymentStatus === 'pending' && (
                                <div className="bg-orange-50 border border-orange-200 rounded-xl p-3 md:p-4">
                                    <div className="flex items-center gap-2 text-orange-700">
                                        <div className="w-4 h-4 border-2 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
                                        <span className="text-sm md:text-base font-medium">Processing payment...</span>
                                    </div>
                                    <p className="text-xs md:text-sm text-orange-600 mt-1">
                                        Please do not close this window until payment is completed
                                    </p>
                                </div>
                            )}
                            
                            {paymentStatus === 'success' && (
                                <div className="bg-green-50 border border-green-200 rounded-xl p-3 md:p-4">
                                    <div className="flex items-center gap-2 text-green-700">
                                        <div className="w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
                                            <svg className="w-2 h-2 text-white" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                            </svg>
                                        </div>
                                        <span className="text-sm md:text-base font-medium">Payment Successful!</span>
                                    </div>
                                    <p className="text-xs md:text-sm text-green-600 mt-1">
                                        Redirecting to your tickets...
                                    </p>
                                </div>
                            )}
                            
                            {paymentStatus === 'failed' && (
                                <div className="bg-red-50 border border-red-200 rounded-xl p-3 md:p-4">
                                    <div className="flex items-center gap-2 text-red-700">
                                        <div className="w-4 h-4 bg-red-500 rounded-full flex items-center justify-center">
                                            <svg className="w-2 h-2 text-white" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                            </svg>
                                        </div>
                                        <span className="text-sm md:text-base font-medium">Payment Failed</span>
                                    </div>
                                    <p className="text-xs md:text-sm text-red-600 mt-1">
                                        Please try again or contact support
                                    </p>
                                </div>
                            )}
                        </div>
                    }
                </Modal.Body>
            </Modal>

            {/* Individual Names Modal */}
            <Modal show={showNames} onHide={handleModalClose} backdrop="static" size="lg" centered keyboard={false} aria-labelledby="contained-modal-title-vcenter">
                <Modal.Header closeButton className='bg-gradient-to-r from-orange-500 to-orange-600'>
                    <Modal.Title className='fs-5 fw-bold !text-white'>Enter Names for Individual Tickets</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="space-y-4">
                    {Array.from({ length: ticketData.qty }, (_, index) => (
                            <div key={index} className="bg-gray-50 rounded-xl p-4">
                                <h5 className='fs-6 font-semibold text-gray-800 mb-3'>Ticket {index + 1}</h5>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm text-gray-600 mb-2">First Name</label>
                                <input
                                    type="text"
                                    value={individualNames[index]?.firstName || ''}
                                    onChange={(e) => handleNameChange(index, 'firstName', e.target.value)}
                                            placeholder={`Enter first name for ticket ${index + 1}`}
                                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200"
                                />
                                    </div>
                                    <div>
                                        <label className="block text-sm text-gray-600 mb-2">Last Name</label>
                                <input
                                    type="text"
                                    value={individualNames[index]?.lastName || ''}
                                            placeholder={`Enter last name for ticket ${index + 1}`}
                                    onChange={(e) => handleNameChange(index, 'lastName', e.target.value)}
                                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200"
                                />
                                    </div>
                            </div>
                        </div>
                    ))}
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleModalClose}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleModalSave} className="bg-orange-500 border-orange-500 hover:bg-orange-600">
                        Save Names
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    )
}

export default TicketPurchase